import { useEffect, useState, useRef } from 'react';

import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type EditorAgent,
	getEditorAgent,
	A11yMateAgent,
	JobWriterAgent,
	CommsCrafterAgent,
} from '@atlassian/generative-ai-modal/utils/agents';

import { useAgents } from '../../../pm-plugins/rovo-agents/useAgents';
import { shouldMockAgentData } from '../../utils/agents/should-mock-agent-data';

type GqlSuccessResponse<T> = {
	data: T;
	errors?: never;
};

type GqlErrorResponse = {
	data: unknown;
	errors?: GqlError[];
};

type GqlError = {
	extensions: {
		classification: string;
		errorSource: string;
		statusCode: number;
	};
	locations: {
		column: number;
		line: number;
	}[];
	message: string;
};

// basic Gql typing - assuming there is no partial success/error
type GqlResponse<T> = GqlSuccessResponse<T> | GqlErrorResponse;

type FavouriteAgentDataType = {
	// icon: string;
	conversationStarters: string[];
	creator: {
		id: string;
	};
	creatorType: string;
	description: string;
	externalConfigReference: string;
	id: string;
	identityAccountId: string;
	name: string;
};

type FavouriteAgentQueryType = {
	agentStudio_getAgents: {
		edges: {
			node: FavouriteAgentDataType;
		}[];
	};
};
type FavouriteResponse = GqlResponse<FavouriteAgentQueryType>;

// coming soon missing `icon`
const favouriteAgentsQuery = `
	query editorPluginAIFavouriteAgents($cloudId: String!, $first: Int, $input: AgentStudioAgentQueryInput) {
		agentStudio_getAgents(
			cloudId: $cloudId
			first: $first
			input: $input
		) @optIn(to: "AgentStudio") {
			edges {
				node {
					...editorPluginAI_getAgentsFragment
				}
			}
		}
	}

	fragment editorPluginAI_getAgentsFragment on AgentStudioAssistant {
		id
		name
		description
		creator {
			id
		}
		creatorType
		# icon
		conversationStarters
		identityAccountId
		externalConfigReference
	}
`;

type RecommendedAgentResponse = {
	agents: Agent[];
};

const mapFavouriteToAgent = (favourite: FavouriteAgentDataType): Agent => {
	return {
		named_id: '',
		creator_type: 'SYSTEM',
		available_plugins: {},
		favourite: true,
		actor_type: 'AGENT',
		user_defined_conversation_starters: [],
		deactivated: false,
		is_default: false,
		...favourite,
		creator: favourite.creator.id,
	};
};

const fetchFavouriteAgents = async ({ cloudId }: { cloudId: string }): Promise<Agent[]> => {
	try {
		const response = await fetch('/gateway/api/graphql', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				query: favouriteAgentsQuery,
				variables: { cloudId, first: 10, input: { onlyFavouriteAgents: true } },
			}),
		});

		const responseJson: FavouriteResponse = await response.json();
		if (responseJson.errors) {
			throw new Error(responseJson.errors[0].message);
		}

		const result = responseJson as GqlSuccessResponse<FavouriteAgentQueryType>;

		return result.data.agentStudio_getAgents.edges.map((edge) => mapFavouriteToAgent(edge.node));
	} catch {
		return [];
	}
};

const fetchRecommendedAgents = async ({ limit }: { limit: number }) => {
	try {
		if (shouldMockAgentData()) {
			return Promise.resolve([CommsCrafterAgent, JobWriterAgent, A11yMateAgent]);
		}
		const response = await fetch(`/gateway/api/assist/rovo/v1/agents/recommend/v2?limit=${limit}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'X-Product': 'confluence',
				'X-Experience-Id': 'ai-mate',
			},
		});
		const result: RecommendedAgentResponse = await response.json();
		return result.agents;
	} catch {
		return [];
	}
};

export const useFavouriteRecommendedFetch = ({
	cloudId,
	editorState,
}: {
	cloudId: string;
	editorState: EditorState;
}): {
	data: EditorAgent[];
	error: Error | null;
	loading: boolean;
} => {
	const [state, setState] = useState<{
		data: EditorAgent[];
		error: Error | null;
		loading: boolean;
	}>({
		data: [],
		loading: true,
		error: null,
	});
	const agents = useAgents({ editorState });
	const agentsInitialised = useRef(agents?.initialised);
	const editorAgents = useRef(agents?.agents);
	agentsInitialised.current = agents?.initialised;
	editorAgents.current = agents?.agents;

	useEffect(() => {
		const fetchFavouriteRecommendedAgents = async () => {
			if (agentsInitialised.current && editorAgents.current) {
				setState({
					data: editorAgents.current,
					loading: false,
					error: null,
				});
				return;
			}
			const favouriteAgents = await fetchFavouriteAgents({ cloudId });

			if (favouriteAgents.length >= 3) {
				setState({
					data: favouriteAgents.map((a) => getEditorAgent(a)),
					loading: false,
					error: null,
				});
				return;
			}

			const recommendedAgents = await fetchRecommendedAgents({ limit: 3 - favouriteAgents.length });

			setState({
				data: [...(favouriteAgents ?? []), ...(recommendedAgents ?? [])].map((a) =>
					getEditorAgent(a),
				),
				loading: false,
				error: null,
			});
		};

		fetchFavouriteRecommendedAgents();
	}, [cloudId]);

	return state;
};
